/**
 * ConfirmDialog.jsx
 * Diálogo de confirmación accesible que reemplaza window.confirm().
 *
 * Uso:
 *   const { confirm } = useConfirm();
 *   const ok = await confirm('¿Eliminar?', { title: 'Confirmar', confirmText: 'Eliminar' });
 *   if (ok) { ... }
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import styles from './ConfirmDialog.module.css';

const ConfirmContext = createContext(null);

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm debe usarse dentro de <ConfirmProvider>');
  }
  return ctx;
};

const DEFAULT_OPTIONS = {
  title: 'Confirmar acción',
  confirmText: 'Aceptar',
  cancelText: 'Cancelar',
  variant: 'danger', // 'danger' | 'info'
};

export const ConfirmProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);

  const confirm = useCallback((message, options = {}) => {
    return new Promise(resolve => {
      setDialog({
        message,
        ...DEFAULT_OPTIONS,
        ...options,
        resolve,
      });
    });
  }, []);

  const handleClose = useCallback((value) => {
    dialog?.resolve(value);
    setDialog(null);
  }, [dialog]);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {dialog && (
        <div className={styles.overlay}>
          <div className={styles.dialog} role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" aria-describedby="confirm-message">
            <h3 id="confirm-title" className={styles.title}>{dialog.title}</h3>
            <p id="confirm-message" className={styles.message}>{dialog.message}</p>
            <div className={styles.actions}>
              <button className={`${styles.button} ${styles.cancel}`} onClick={() => handleClose(false)}>
                {dialog.cancelText}
              </button>
              <button className={`${styles.button} ${styles.confirm} ${dialog.variant === 'info' ? styles.info : ''}`} onClick={() => handleClose(true)} autoFocus>
                {dialog.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;
